import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Users, ClipboardList } from 'lucide-react';
import api from '../api';
import { Avatar, ListSkeleton, EmptyState } from '../components/ui';
import { usePresence } from '../context/PresenceContext';

const colori = ['#a855f7','#00d4ff','#10b981','#f59e0b','#f43f5e','#3b82f6','#ec4899'];
function color(nome) { return colori[nome.charCodeAt(0) % colori.length]; }
function initials(nome, cognome) { return ((nome||'')[0]+(cognome||'')[0]).toUpperCase(); }

function fmtData(d) {
  if (!d) return null;
  const days = ['dom','lun','mar','mer','gio','ven','sab'];
  const dt = new Date(d);
  return days[dt.getDay()];
}

export default function Team() {
  const navigate = useNavigate();
  const { isOnline } = usePresence();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userTasks, setUserTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);

  useEffect(() => {
    api.get('/team').then(r => { setTeam(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const openUser = async (u) => {
    setSelectedUser(u);
    setLoadingTasks(true);
    try {
      const r = await api.get(`/team/${u.id}/task`);
      setUserTasks(r.data);
    } catch {}
    setLoadingTasks(false);
  };

  return (
    <div className="eden-page">
      <div style={{ marginBottom:24 }}>
        <h1 style={{ fontSize:22, fontWeight:700, margin:'0 0 4px', color:'var(--text)' }}>Team</h1>
        <p style={{ fontSize:13, color:'var(--text-muted)', margin:0 }}>Agenzia · task futuri e svolti per persona.</p>
      </div>

      {loading ? (
        <ListSkeleton rows={5} />
      ) : team.length === 0 ? (
        <EmptyState icon={Users} title="Nessun membro del team" hint="Gli utenti dell'agenzia compaiono qui con i loro task." />
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(240px,1fr))', gap:12 }}>
          {team.map(u => {
            return (
              <div key={u.id} onClick={() => openUser(u)}
                style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:14, padding:'16px 18px', cursor:'pointer', transition:'border-color 0.15s, background 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(0,212,255,0.3)'; e.currentTarget.style.background='var(--surface-h)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.background='var(--surface)'; }}
              >
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
                  <Avatar name={`${u.nome || ''} ${u.cognome || ''}`} size={36} online={isOnline(u.id)} />
                  <div>
                    <div style={{ fontSize:13.5, fontWeight:600, color:'var(--text)' }}>{u.nome} {u.cognome}</div>
                    <div style={{ fontSize:11, color: isOnline(u.id) ? 'var(--green)' : 'var(--text-muted)', textTransform:'capitalize' }}>
                      {isOnline(u.id) ? 'Online' : u.ruolo}
                    </div>
                  </div>
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:10 }}>
                  <div style={{ background:'var(--bg3)', borderRadius:8, padding:'8px 10px', textAlign:'center' }}>
                    <div style={{ fontSize:18, fontWeight:700, color:'var(--text)' }}>{u.task_futuri || 0}</div>
                    <div style={{ fontSize:10, color:'var(--text-muted)' }}>futuri</div>
                  </div>
                  <div style={{ background:'var(--bg3)', borderRadius:8, padding:'8px 10px', textAlign:'center' }}>
                    <div style={{ fontSize:18, fontWeight:700, color:'var(--green)' }}>{u.task_svolti || 0}</div>
                    <div style={{ fontSize:10, color:'var(--text-muted)' }}>svolti</div>
                  </div>
                </div>

                {u.prossimo_task && (
                  <div style={{ fontSize:11.5, color:'var(--text-muted)' }}>
                    <span style={{ color:'var(--text-dim)' }}>Prossimo: </span>
                    {u.prossimo_task}
                    {u.prossima_scadenza && <span style={{ color:'var(--cyan)', marginLeft:5 }}>· {fmtData(u.prossima_scadenza)}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Panel task utente */}
      {selectedUser && (
        <div style={{
          position:'fixed', top:0, right:0, width:'min(420px, 100vw)', height:'100vh',
          background:'var(--bg2)', borderLeft:'1px solid var(--border)',
          display:'flex', flexDirection:'column', zIndex:500,
          boxShadow:'-8px 0 32px rgba(0,0,0,0.4)',
        }}>
          <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div>
              <div style={{ fontSize:15, fontWeight:700, color:'var(--text)' }}>{selectedUser.nome} {selectedUser.cognome}</div>
              <div style={{ fontSize:11.5, color:'var(--text-muted)', textTransform:'capitalize' }}>{selectedUser.ruolo}</div>
            </div>
            <button onClick={() => setSelectedUser(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:6, display:'flex' }}>
              <X size={16}/>
            </button>
          </div>
          <div style={{ flex:1, overflowY:'auto', padding:'16px 20px' }}>
            {loadingTasks ? (
              <ListSkeleton rows={3} />
            ) : userTasks.length === 0 ? (
              <EmptyState icon={ClipboardList} title="Nessun task" hint="Questa persona non ha task assegnati." />
            ) : userTasks.map(t => (
              <div key={t.id} onClick={() => navigate(`/task?open=${t.id}`)}
                style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 14px', marginBottom:8, cursor:'pointer', transition:'border-color 0.1s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor='rgba(0,212,255,0.3)'}
                onMouseLeave={e => e.currentTarget.style.borderColor='var(--border)'}
              >
                <div style={{ fontSize:13, fontWeight:500, color:'var(--text)', marginBottom:4 }}>{t.titolo}</div>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <span style={{ fontSize:11, color:'var(--text-muted)' }}>{t.stato?.replace('_',' ')}</span>
                  {t.cliente_nome && <span style={{ fontSize:11, color:'var(--cyan)' }}>{t.cliente_nome}</span>}
                  {t.data_scadenza && <span style={{ fontSize:11, color:'var(--text-dim)', marginLeft:'auto' }}>{new Date(t.data_scadenza).toLocaleDateString('it-IT')}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
